import React, { useEffect, useState } from 'react';
import Session from '../../utils/Session.js';
import { GET_SCHOOL } from '../../Graphql/School.js';
import { GET_STUDENTS } from '../../Graphql/Pupil.js';
import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { BlockButton, Loading, NavBar } from '../PageElements.js';
import { onPageLoad } from '../../utils/Auth.js';
import Redirect from '../../utils/Redirect.js';
import { Menu } from '../../utils/Auth.js';
import { APIResponse, getImage } from '../../utils/Helpers.js';
import UserContext from '../../utils/UserContext.js';

const Classroom = React.lazy(() => import('../classroom/Classroom.js'));
const Staff = React.lazy(() => import('./Staff.js'));
const Messages = React.lazy(() => import('../messages/Messages.js'));
const Employee = React.lazy(() => import('../employees/Employee.js'));
const Birthdays = React.lazy(() => import('./Birthdays.js'));
const Calendar = React.lazy(() => import('./Calendar.js'));
const Reports = React.lazy(() => import('../reports/Reports.js'));
const CreatePupil = React.lazy(() => import('../pupils/CreatePupil.js'));
const EditPupil = React.lazy(() => import('../pupils/EditPupil.js'));
const Parents = React.lazy(() => import('../pupils/Parents.js'));
const Share = React.lazy(() => import('../../utils/Share.js'));
const Pupils = React.lazy(() => import('../pupils/Pupils.js'));
const EditRoom = React.lazy(() => import('../classroom/EditRoom.js'));
const MyClass = React.lazy(() => import('../classroom/MyClass.js'));
const ClassroomReportCards = React.lazy(() => import('../classroom/ClassroomReportCards.js'));
const PostActivity = React.lazy(() => import('../classroom/PostActivity.js'));
const Activity = React.lazy(() => import('../classroom/Activity.js'));
const ParentMessage = React.lazy(() => import('../messages/ParentMessage.js'));
const Compose = React.lazy(() => import('../messages/Compose.js'));
const EditEmployee = React.lazy(() => import('../employees/EditEmployee.js'));
const Logs = React.lazy(() => import('../employees/Logs.js'));
const FixedAsset = React.lazy(() => import('./FixedAsset.js'));
// const CBT = React.lazy(() => import('../CBT/CBT.js'));
const Preview = React.lazy(() => import('../CBT/Preview.js'));
const ParentInvite = React.lazy(() => import('../pupils/ParentInvite.js'));
const ClassAttendance = React.lazy(() => import('../reports/school/ClassAttendance.js'));
const PupilAttendance = React.lazy(() => import('../reports/PupilAttendance.js'));
const ActivityTrend = React.lazy(() => import('../reports/ActivityTrend.js'));
const MessageTrend = React.lazy(() => import('../reports/MessageTrend.js'));
const ResultTemplate = React.lazy(() => import('./ResultTemplate.js'));
const CreateReportCards = React.lazy(() => import('./CreateReportCards.js'));
const Results = React.lazy(() => import('../results/Results.js'));
const SyllabuxVideos = React.lazy(() => import('../learningAids/syllabuxVideos.js'));
const PlayGame = React.lazy(() => import('../learningAids/PlayGame.js'));
const GameSettings = React.lazy(() => import('../learningAids/GameSettings.js'));
const LearningAidsAssigned = React.lazy(() => import('../learningAids/LearningAidsAssigned.js'));
const Bills = React.lazy(() => import('../bill/Bills.js'));
const TeacherVideo = React.lazy(() => import('../reports/TeacherVideos.js'));
const ComposeMultiple = React.lazy(() => import('../messages/ComposeMultiple.js'));
const RealTimeAttendance = React.lazy(() => import('../reports/RealTimeAttendance.js'));
const TeacherCBT = React.lazy(() => import('../reports/TeacherCBT.js'));
const TeacherAID = React.lazy(() => import('../reports/TeacherAID.js'));
const TeacherActivity = React.lazy(() => import('../reports/TeacherActivity.js'));
const TeacherMessage = React.lazy(() => import('../reports/TeacherMessage.js'));
const ParentMessageReport = React.lazy(() => import('../reports/school/ParentMessageReport.js'));
const VideoPlay = React.lazy(() => import('./VideoPlay.js'));
const EditAttendance = React.lazy(() => import('./EditAttendance.js'));
const ClearSmartCards = React.lazy(() => import('../settings/ClearSmartCards.js'));
const Kiosk = React.lazy(() => import('./Kiosk.js'));
const AssignCards = React.lazy(() => import('../settings/AssignCards.js'));
const TeacherAttendance = React.lazy(() => import('../reports/TeacherAttendance.js'));
const EmployeePunctuality = React.lazy(() => import('../reports/EmployeePunctuality.js'));
const GenderDistribution = React.lazy(() => import('../reports/school/GenderDistribution.js'));
const AgeDistribution = React.lazy(() => import('../reports/school/AgeDistribution.js'));
const NewQuestionBank = React.lazy(() => import('../CBT/NewQuestionBank.js'));
const ManageQuestionBank = React.lazy(() => import('../CBT/ManageQuestionBank.js'));
const AssignedQuestions = React.lazy(() => import('../CBT/AssignedQuestions.js'));
const CBTVisibility = React.lazy(() => import('../CBT/CBTVisibility.js'));
const CBTSpreadsheet = React.lazy(() => import('../CBT/CBTSpreadsheet.js'));
const LessonPlan = React.lazy(() => import('../lessonPlan/LessonPlan.js'));
const SchoolVideos = React.lazy(() => import('../learningAids/schoolVideos.js'));
const SyllabuxGames = React.lazy(() => import('../learningAids/syllabuxGames.js'));
const InactivePupils = React.lazy(() => import('../pupils/InactivePupils.js'));
const Subscription = React.lazy(() => import('../settings/Subscription.js'));
const VirtualClass = React.lazy(() => import('../virtualClass/VirtualClass.js'));
const EditVirtualClass = React.lazy(() => import('../virtualClass/EditVirtualClass.js'));
const UploadPupils = React.lazy(() => import('../pupils/UploadPupils.js'));
const ParentReport = React.lazy(() => import('../reports/school/ParentReport.js'));
const Uploadreportcard = React.lazy(() => import('./Uploadreportcard.js'));
const ProspectivePupils = React.lazy(() => import('../pupils/prospectivePupils.js'));
const QuickScheme = React.lazy(() => import('../lessonPlan/QuickScheme.js'));
const StudentPerformance = React.lazy(() => import('../pupils/StudentPerformance.js'));
const NewPerformanceReport = React.lazy(() => import('../pupils/NewPerformanceReport.js'));
const PerformanceList = React.lazy(() => import('../pupils/PerformanceList.js'));
const WeeklyConductForm = React.lazy(() => import('../weeklyConduct/WeeklyConductForm.js'));
const WeeklyConductList = React.lazy(() => import('../weeklyConduct/WeeklyConductList.js'));
const WeeklyConductSettings = React.lazy(() => import('../weeklyConduct/WeeklyConductSettings.js'));
const ManualBill = React.lazy(() => import('../manualBill/ManualBill.js'));
const SMS = React.lazy(() => import('./SMS.js'));
const Promotion = React.lazy(() => import('../pupils/Promotion.js'));
const Debtors = React.lazy(() => import('../accounts/Debtors.js'));
const EditPlan = React.lazy(() => import('../lessonPlan/EditPlan.js'));
const LessonReport = React.lazy(() => import('../reports/school/LessonReport.js'));
const TeacherConcluded = React.lazy(() => import('../reports/staff/TeacherConcluded.js'));
const NewReportCard = React.lazy(() => import('../results/NewReportCard.js'));
const ReportCardV3 = React.lazy(() => import('../results/ReportCardV3.js'));
const ReportCardV4 = React.lazy(() => import('../results/ReportCardV4.js'));
const SubjectAssessments = React.lazy(() => import('../results/SubjectAssesssments.js'));
const AssessmentTypes = React.lazy(() => import('../results/AssessmentTypes.js'));
const MergeResults = React.lazy(() => import('../results/MergeResults.js'));
const ManualDebtors = React.lazy(() => import('../accounts/ManualDebtors.js'));
const UploadDocument = React.lazy(() => import('./UploadDocument.js'));

const Documents = React.lazy(() => import('./Documents.js'));
const PupilsResults = React.lazy(() => import('../results/PupilsResults.js'));
const Transcript = React.lazy(() => import('../results/Transcript.js'));
const ClassroomBroadsheet = React.lazy(() => import('../results/ClassroomBroadsheet.js'));
// const FaceCapture = React.lazy(() => import('./FaceCapture.js'));
const CardAttendance = React.lazy(() => import('./CardAttendance.js'));
// const FaceAttendance = React.lazy(() => import('./FaceAttendance.js'));
const BarcodeAttendance = React.lazy(() => import('./BarcodeAttendance.js'));
const CreatePlan = React.lazy(() => import('../lessonPlan/CreatePlan.js'));
const EditInactivePupils = React.lazy(() => import('../pupils/EditInactivePupil.js'));
const BookClub = React.lazy(() => import('../bookClub/bookClub.js'));
const News = React.lazy(() => import('./News.js'));
const ManageInventoryCategory = React.lazy(() => import('../inventory/ManageInventoryCategory.js'));
const ManageInventoryLocation = React.lazy(() => import('../inventory/ManageInventoryLocation.js'));
const ManageInventoryItem = React.lazy(() => import('../inventory/ManageInventoryItem.js'));

const InventoryStock = React.lazy(() => import('../inventory/InventoryStock.js'));
const InventoryStockV2 = React.lazy(() => import('../inventory/InventoryStockV2.js'));
const InventoryBalances = React.lazy(() => import('../inventory/InventoryBalances.js'));
const InventoryHistory = React.lazy(() => import('../inventory/InventoryHistory.js'));
const InventoryLocationReport = React.lazy(() => import('../inventory/InventoryLocationReport.js'));
const InventoryItemReport = React.lazy(() => import('../inventory/InventoryItemReport.js'));
const InventoryReorderReport = React.lazy(() => import('../inventory/InventoryReorderReport.js'));
const InventoryItemList = React.lazy(() => import('../inventory/InventoryItemList.js'));
const Inventory = React.lazy(() => import('../inventory/Inventory.js'));
const NewTemplates = React.lazy(() => import('../results/NewTemplates.js'));
const InventoryBatches = React.lazy(() => import('../inventory/InventoryBatches.js'));
const SalesHistory = React.lazy(() => import('../inventory/SalesHistory.js'));
const StudentGroups = React.lazy(() => import('../pupils/StudentGroups.js'));
const StudentGroupDetails = React.lazy(() => import('../pupils/StudentGroupDetails.js'));
const CreateStudentGroup = React.lazy(() => import('../pupils/CreateStudentGroup.js'));
const AddMembersToStudentGroup = React.lazy(() => import('../pupils/AddMembersToStudentGroup.js'));
const TemplateBank = React.lazy(() => import('../results/TemplateBank.js'));

export const SchoolHome = () => {

};

const School = () => {
  const myContext = React.useContext(UserContext);
  const schoolID = +myContext.school.id;
  if (!schoolID) Redirect('/dashboard');
  const nav = useNavigate();
  let { resourceId, cbtID } = useParams();

  // eslint-disable-next-line no-unused-vars
  const [extraMenu, setExtraMenu] = useState();
  const [showNav, setShowNav] = useState(true);
  const [loaded, setLoaded] = useState(0);

  useEffect(() => {
    Session.showAlert();
  }, ['']);

  useEffect(() => {
    window.scrollTo(0, 0);
    onPageLoad(myContext) || nav('/dashboard');
    document.title = myContext.school.name || '';

  }, [myContext.school.name]);

  useEffect(() => {
    const plan = myContext.school.plan;
    const activePlan = myContext.objects?.CONFIG?.plans.find(p => p.name === plan);
    if (!activePlan || !plan) {
      return
    }

    const maxStudents = activePlan.maxStudents;
    if (myContext.objects?.pupils?.length > maxStudents) {
      Session.showAlert(`The student limit of ${maxStudents} for ${myContext.school.name} has been reached on ${plan} plan. To accommodate more students, please consider upgrading to a higher plan. Note that ${myContext.objects.pupils.length - maxStudents} students have been randomly excluded from your student list.`);
      let students = myContext.objects.pupils;
      students = students.sort(() => Math.random() - 0.5);
      students.length = maxStudents;
      myContext.setObjects({ ...myContext.objects, pupils: students.sort((a, b) => a.lastname.localeCompare(b.lastname)) });

    }

  }, [myContext.objects.pupils, myContext.school]);



  const refetchAll = () => {
    refetchStudents();
    refetch();
  };






  // Get school from gql
  const { loading, refetch, networkStatus } = useQuery(GET_SCHOOL, {
    variables: { schoolID },
    fetchPolicy: 'network-only', //cache-first, cache-only, cache-and-network, network-only, no-cache, standby
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      let latestNews = 0;
      data.getSchool.news.forEach(element => {
        if (element.id > latestNews) latestNews = +element.id;
      });
      const oldLatestNews = +Session.value('schoolLatestNews');
      if (latestNews > oldLatestNews) {
        Session.setValue('schoolLatestNews', latestNews);
        Session.showAlert('New app updates available. Check "Newsfeed" menu.');
      }
      myContext.setSchool(data.getSchool);
      setLoaded(loaded + 1);
    },
    onError: error => {
      Session.alert('Unable to fetch school data');

      APIResponse(error);
      Session.showAlert();
    }
  });


  // Get pupils with apollo from gql
  const { loading: loadingStudents, networkStatus: networkStatusStudents } = useQuery(GET_STUDENTS, {
    variables: { schoolID: +schoolID },
    fetchPolicy: 'network-only', //cache-first, cache-only, cache-and-network, network-only, no-cache, standby
    // nextFetchPolicy: 'network-only', // Used for subsequent executions
    // variables: { breed },
    onCompleted: data => {
      const results = [...data.getStudentsBySchool].map(p => {
        return { ...p, ...p.details };
      }).sort((a, b) => a.lastname.localeCompare(b.lastname));
      setLoaded(loaded + 1);

      myContext.setObjects({ ...myContext.objects, pupils: results });
    },
    onError: error => {
      Session.alert('Unable to fetch pupils.');
      APIResponse(error);
      Session.showAlert();
    }
  });
  // Get pupils with apollo from gql
  const [refetchStudents] = useLazyQuery(GET_STUDENTS, {
    variables: { schoolID: +schoolID },
    fetchPolicy: 'network-only', //cache-first, cache-only, cache-and-network, network-only, no-cache, standby
    nextFetchPolicy: 'network-only', // Used for subsequent executions
    // variables: { breed },
    onCompleted: data => {
      const results = [...data.getStudentsBySchool].map(p => {
        return { ...p, ...p.details };
      }).sort((a, b) => a.lastname.localeCompare(b.lastname));
      setLoaded(loaded + 1);

      // Store for diagnostic access in PupilsResults.js
      window.__lastRefetchedPupils = results;

      myContext.setObjects({ ...myContext.objects, pupils: results });
      // Session.setObject(schoolID + 'P', results)
      // setMainParams({ ...mainParams, pupils: results })
    },
    onError: error => {
      Session.alert('Unable to fetch pupils.');
      APIResponse(error);
      Session.showAlert();
    }
  });


  // eslint-disable-next-line no-unused-vars
  const [mainParams, setMainParams] = useState({
    refetchAll,
    setExtraMenu,
    refetchStudents,
    refetchSchool: refetch,
    setShowNav
  });

  if (loading || loadingStudents) return <Loading />;


  const route = () => {
    if (myContext.school.plan != 'FREE' && DateTime.fromSQL(myContext.school.expiry) < DateTime.now()) return <React.Suspense fallback={<Loading />}><Subscription mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'classroom') return <React.Suspense fallback={<Loading />}><Classroom key='Classroom' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'editroom') return <React.Suspense fallback={<Loading />}><EditRoom key='EditRoom' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'pupils') return <React.Suspense fallback={<Loading />}><Pupils key='Pupils' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'staff') return <React.Suspense fallback={<Loading />}><Staff key='Staff' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'messages') return <React.Suspense fallback={<Loading />}><Messages key='Messages' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'employee') return <React.Suspense fallback={<Loading />}><Employee key='Employee' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'editemployee') return <React.Suspense fallback={<Loading />}><EditEmployee key='EditEmployee' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'logs') return <React.Suspense fallback={<Loading />}><Logs key='Logs' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'birthdays') return <React.Suspense fallback={<Loading />}><Birthdays key='Birthdays' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'calendar') return <React.Suspense fallback={<Loading />}><Calendar key='Calendar' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'reports') return <React.Suspense fallback={<Loading />}><Reports key='Reports' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'createpupil') return <React.Suspense fallback={<Loading />}><CreatePupil key='CreatePupil' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'editpupil') return <React.Suspense fallback={<Loading />}><EditPupil key='EditPupil' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'parents') return <React.Suspense fallback={<Loading />}><Parents key='Parents' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'share') return <React.Suspense fallback={<Loading />}><Share key='Share' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'myclass') return <React.Suspense fallback={<Loading />}><MyClass key='MyClass' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'classroom-reportcards') return <React.Suspense fallback={<Loading />}><ClassroomReportCards key='ClassroomReportCards' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'postactivity') return <React.Suspense fallback={<Loading />}><PostActivity key='PostActivity' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'activity') return <React.Suspense fallback={<Loading />}><Activity key='Activity' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'parentmessage') return <React.Suspense fallback={<Loading />}><ParentMessage key='ParentMessage' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'compose') return <React.Suspense fallback={<Loading />}><Compose key='Compose' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'composemultiple') return <React.Suspense fallback={<Loading />}><ComposeMultiple key='ComposeMultiple' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'subscription') return <React.Suspense fallback={<Loading />}><Subscription mainParams={mainParams} /></React.Suspense>;
    // else if (resourceId === 'cbt') return <React.Suspense fallback={<Loading />}><CBT key='CBT' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'cbtpreview') return <React.Suspense fallback={<Loading />}><Preview key='Preview' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'parentinvite') return <React.Suspense fallback={<Loading />}><ParentInvite key='ParentInvite' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'fixedasset') return <React.Suspense fallback={<Loading />}><FixedAsset key='FixedAsset' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'inactivepupils') return <React.Suspense fallback={<Loading />}><InactivePupils mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'classattendance') return <React.Suspense fallback={<Loading />}><ClassAttendance mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'teacherattendance') return <React.Suspense fallback={<Loading />}><TeacherAttendance mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'pupilattendance') return <React.Suspense fallback={<Loading />}><PupilAttendance mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'employeepunctuality') return <React.Suspense fallback={<Loading />}><EmployeePunctuality mainParams={mainParams} /></React.Suspense>;

    else if (resourceId === 'genderdistribution') return <React.Suspense fallback={<Loading />}><GenderDistribution mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'agedistribution') return <React.Suspense fallback={<Loading />}><AgeDistribution mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'activitytrend') return <React.Suspense fallback={<Loading />}><ActivityTrend mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'messagetrend') return <React.Suspense fallback={<Loading />}><MessageTrend mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'realtimeattendance') return <React.Suspense fallback={<Loading />}><RealTimeAttendance mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'teachervideo') return <React.Suspense fallback={<Loading />}><TeacherVideo mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'bills') return <React.Suspense fallback={<Loading />}><Bills key='Bills' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'resulttemplate') return <React.Suspense fallback={<Loading />}><ResultTemplate key='resultTemplate' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'createreportcards') return <React.Suspense fallback={<Loading />}><CreateReportCards key='createreportcards' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'uploadreportcards') return <React.Suspense fallback={<Loading />}><Uploadreportcard mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'results') return <React.Suspense fallback={<Loading />}><Results key='results' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'syllabuxvideos') return <React.Suspense fallback={<Loading />}><SyllabuxVideos mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'syllabuxgames') return <React.Suspense fallback={<Loading />}><SyllabuxGames mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'schoolvideos') return <React.Suspense fallback={<Loading />}><SchoolVideos mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'gamesettings') return <React.Suspense fallback={<Loading />}><GameSettings key='gamesettings' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'playgame') return <React.Suspense fallback={<Loading />}><PlayGame key='results' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'learningaidsassigned') return <React.Suspense fallback={<Loading />}><LearningAidsAssigned key='results' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'teachercbt') return <React.Suspense fallback={<Loading />}><TeacherCBT key='reports' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'teacheraid') return <React.Suspense fallback={<Loading />}><TeacherAID key='reports' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'teacheractivity') return <React.Suspense fallback={<Loading />}><TeacherActivity key='reports' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'teachermessage') return <React.Suspense fallback={<Loading />}><TeacherMessage key='reports' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'parentmessagereport') return <React.Suspense fallback={<Loading />}><ParentMessageReport key='reports' mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'video') return <React.Suspense fallback={<Loading />}><VideoPlay key='video' mainParams={mainParams} /></React.Suspense>;

    else if (resourceId === 'editattendance') return <React.Suspense fallback={<Loading />}><EditAttendance mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'assigncards') return <React.Suspense fallback={<Loading />}><AssignCards mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'clearsmartcards') return <React.Suspense fallback={<Loading />}><ClearSmartCards mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'newquestionbank') return <React.Suspense fallback={<Loading />}><NewQuestionBank mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'managequestionbank') return <React.Suspense fallback={<Loading />}><ManageQuestionBank mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'assignedquestions') return <React.Suspense fallback={<Loading />}><AssignedQuestions mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'cbt-visibility') return <React.Suspense fallback={<Loading />}><CBTVisibility mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'lessonplan') return <React.Suspense fallback={<Loading />}><LessonPlan mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'kiosk') return <React.Suspense fallback={<Loading />}><Kiosk mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'virtualclass') return <React.Suspense fallback={<Loading />}><VirtualClass mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'editvirtualclass') return <React.Suspense fallback={<Loading />}><EditVirtualClass mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'uploadpupils') return <React.Suspense fallback={<Loading />}><UploadPupils mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'parentreport') return <React.Suspense fallback={<Loading />}><ParentReport mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'prospectivepupils') return <React.Suspense fallback={<Loading />}><ProspectivePupils mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'quickscheme') return <React.Suspense fallback={<Loading />}><QuickScheme mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'sms') return <React.Suspense fallback={<Loading />}><SMS /></React.Suspense>;
    else if (resourceId === 'promotion') return <React.Suspense fallback={<Loading />}><Promotion mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'debtors') return <React.Suspense fallback={<Loading />}><Debtors /></React.Suspense>;
    else if (resourceId === 'editplan') return <React.Suspense fallback={<Loading />}><EditPlan /></React.Suspense>;
    else if (resourceId === 'lessonreport') return <React.Suspense fallback={<Loading />}><LessonReport /></React.Suspense>;
    else if (resourceId === 'teacherconcluded') return <React.Suspense fallback={<Loading />}><TeacherConcluded /></React.Suspense>;
    else if (resourceId === 'newreportcard') return <React.Suspense fallback={<Loading />}><NewReportCard mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'reportcardv3') return <React.Suspense fallback={<Loading />}><ReportCardV3 mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'reportcardv4') return <React.Suspense fallback={<Loading />}><ReportCardV4 mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'assessmenttypes') return <React.Suspense fallback={<Loading />}><AssessmentTypes mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'subjectassessments') return <React.Suspense fallback={<Loading />}><SubjectAssessments mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'pupils-results') return <React.Suspense fallback={<Loading />}><PupilsResults mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'transcripts') return <React.Suspense fallback={<Loading />}><Transcript mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'classroom-broadsheet') return <React.Suspense fallback={<Loading />}><ClassroomBroadsheet /></React.Suspense>;

    else if (resourceId === 'mergeresults') return <React.Suspense fallback={<Loading />}><MergeResults mainParams={mainParams} /></React.Suspense>;

    else if (resourceId === 'newperformancereport') return <React.Suspense fallback={<Loading />}><NewPerformanceReport mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'studentperformance') return <React.Suspense fallback={<Loading />}><StudentPerformance mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'performancelist') return <React.Suspense fallback={<Loading />}><PerformanceList mainParams={mainParams} /></React.Suspense>;

    else if (resourceId === 'weeklyconduct') return <React.Suspense fallback={<Loading />}><WeeklyConductForm mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'weeklyconductlist') return <React.Suspense fallback={<Loading />}><WeeklyConductList mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'weeklyconductsettings') return <React.Suspense fallback={<Loading />}><WeeklyConductSettings mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'manualbill') return <React.Suspense fallback={<Loading />}><ManualBill mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'manualdebtors') return <React.Suspense fallback={<Loading />}><ManualDebtors mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'uploaddocuments') return <React.Suspense fallback={<Loading />}><UploadDocument /></React.Suspense>;
    else if (resourceId === 'documents') return <React.Suspense fallback={<Loading />}><Documents /></React.Suspense>;
    // else if (resourceId === 'facecapture') return <React.Suspense fallback={<Loading />}><FaceCapture /></React.Suspense>;
    else if (resourceId === 'cardattendance') return <React.Suspense fallback={<Loading />}><CardAttendance /></React.Suspense>;
    // else if (resourceId === 'faceattendance') return <React.Suspense fallback={<Loading />}><FaceAttendance /></React.Suspense>;
    else if (resourceId === 'barcodeattendance') return <React.Suspense fallback={<Loading />}><BarcodeAttendance /></React.Suspense>;
    else if (resourceId === 'createplan') return <React.Suspense fallback={<Loading />}><CreatePlan /></React.Suspense>;
    else if (resourceId === 'editinactivepupils') return <React.Suspense fallback={<Loading />}><EditInactivePupils /></React.Suspense>;
    else if (resourceId === 'bookclub') return <React.Suspense fallback={<Loading />}><BookClub /></React.Suspense>;
    else if (resourceId === 'news') return <React.Suspense fallback={<Loading />}><News /></React.Suspense>;

    else if (resourceId === 'inventorycategory') return <React.Suspense fallback={<Loading />}><ManageInventoryCategory /></React.Suspense>;
    else if (resourceId === 'inventorylocation') return <React.Suspense fallback={<Loading />}><ManageInventoryLocation /></React.Suspense>;
    else if (resourceId === 'inventoryitems') return <React.Suspense fallback={<Loading />}><ManageInventoryItem /></React.Suspense>;
    else if (resourceId === 'inventorystock') return <React.Suspense fallback={<Loading />}><InventoryStock /></React.Suspense>;
    else if (resourceId === 'inventorystockv2') return <React.Suspense fallback={<Loading />}><InventoryStockV2 /></React.Suspense>;
    else if (resourceId === 'inventory') return <React.Suspense fallback={<Loading />}><Inventory /></React.Suspense>;
    else if (resourceId === 'inventorybalances') return <React.Suspense fallback={<Loading />}><InventoryBalances /></React.Suspense>;
    else if (resourceId === 'inventoryhistory') return <React.Suspense fallback={<Loading />}><InventoryHistory /></React.Suspense>;
    else if (resourceId === 'inventorylocationreport') return <React.Suspense fallback={<Loading />}><InventoryLocationReport /></React.Suspense>;
    else if (resourceId === 'inventoryitemreport') return <React.Suspense fallback={<Loading />}><InventoryItemReport /></React.Suspense>;
    else if (resourceId === 'inventoryreorderreport') return <React.Suspense fallback={<Loading />}><InventoryReorderReport /></React.Suspense>;
    else if (resourceId === 'inventoryitemlist') return <React.Suspense fallback={<Loading />}><InventoryItemList /></React.Suspense>;
    else if (resourceId === 'managetemplates') return <React.Suspense fallback={<Loading />}><NewTemplates mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'templatebank') return <React.Suspense fallback={<Loading />}><TemplateBank mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'studentgroups') return <React.Suspense fallback={<Loading />}><StudentGroups mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'studentgroupdetails') return <React.Suspense fallback={<Loading />}><StudentGroupDetails mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'createstudentgroup') return <React.Suspense fallback={<Loading />}><CreateStudentGroup mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'addmemberstostudentgroup') return <React.Suspense fallback={<Loading />}><AddMembersToStudentGroup mainParams={mainParams} /></React.Suspense>;
    else if (resourceId === 'inventorybatches') return <React.Suspense fallback={<Loading />}><InventoryBatches /></React.Suspense>;
    else if (resourceId === 'saleshistory') return <React.Suspense fallback={<Loading />}><SalesHistory /></React.Suspense>;
    else if (cbtID || (resourceId === 'cbt-results')) return <React.Suspense fallback={<Loading />}><CBTSpreadsheet mainParams={mainParams} /></React.Suspense>;

    else return <React.Suspense fallback={<Loading />}><div className='container px-0'><Menu mainParams={mainParams} /></div></React.Suspense>;
  };

  // check access and set links
  return (
    <div className='w3-animate-opacity'>
      {showNav && <NavBar>

        <Link onClick={() => setExtraMenu('')} to="/school" className="list-group-item list-group-item-action">School Menu</Link>
      </NavBar>}
      <div className=''>
        {!resourceId &&
          <div className='container d-flex text-start px-0 py-2 mb-2'>
            <img src={getImage(myContext.school?.logo)} className='hm-50 w3-round-large mx-3 mb-md-0' alt='School Logo' />
            <div>
              <h3 className='w3-xlarge brand mb-0 mt-0'>{myContext.school?.name}</h3>
              <div className='w3-opacity'>{myContext.school?.website || myContext.school?.phone}</div>
            </div>

          </div>
        }

        {(loading ||
          loadingStudents ||
          networkStatus === NetworkStatus.refetch ||
          networkStatusStudents === NetworkStatus.refetch) && <Loading _fullPage={false} />}
        {
          myContext.school.name?.length < 3 && !loading && networkStatus !== NetworkStatus.refetch &&
          <div className='container'>
            <div className='w3-container mt-5 pt-5'>
              <BlockButton _method={() => { nav('/dashboard'); }} _name="Reload" />
            </div>
          </div>
        }
        {myContext.school.name?.length >= 3 && <div className='container'>{route()}</div>}

      </div>
    </div>
  );
};

export default School;